import { useLocation } from '@reach/router'
import { TFunctionResult } from 'i18next'
import React, { SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'

import Box, { BoxProps } from '@/modules/components/Box'
import Flex from '@/modules/components/Flex'
import Icon from '@/modules/components/Icon'
import Typography from '@/modules/components/Typography'
import { LowerRegions, lowerRegions } from '@/modules/configs/region'
import { getRegionPath } from '@/modules/utils'

import { Modal } from './Modal'

type FirstTimePopupProps = {
  isOpen: boolean
  setOpen: React.Dispatch<SetStateAction<boolean>>
  isMobile: boolean
  allRegion: LowerRegions[]
  onClick: (region: string) => void
} & Omit<BoxProps, 'onClick'>

export const FirstTimePopup = ({
  isOpen,
  setOpen,
  isMobile,
  allRegion,
  onClick,
  ...restProps
}: FirstTimePopupProps) => {
  const { t } = useTranslation('navigation')

  const location = useLocation()
  const regionPath = getRegionPath(location.pathname)

  const getRegionFlagIcon = (region: string) => {
    const flagStr = `${region.toUpperCase()}Flag`
    const RegionIcon = (Icon as any)[flagStr]
    return React.createElement(RegionIcon, {
      fontSize: '24px',
      border:
        regionPath === region || (region === 'global' && regionPath === '')
          ? '1.5px solid #1199FA'
          : '1.5px solid #001b3c',
      borderRadius: '50%',
    })
  }

  const renderRegionItem = (region: LowerRegions) => {
    return (
      <>
        {getRegionFlagIcon(region)}
        <Typography
          variant="subtitle3"
          color={
            regionPath === region || (region === 'global' && regionPath === '')
              ? 'primaryBrightBlue'
              : '#0b1426'
          }
          maxWidth={['200px', 'max-content']}
          className="text"
          mx="6px"
        >
          {t(`geolocation.${region}`)}
        </Typography>
        <Typography
          variant="subtitle4"
          color={
            regionPath === region || (region === 'global' && regionPath === '')
              ? 'primaryBrightBlue'
              : '#656E85'
          }
        >
          {region.toUpperCase()}
        </Typography>
      </>
    )
  }

  const renderRegionList = (region: LowerRegions) => (
    <Flex
      key={region}
      onClick={() => {
        //  TODO improve lower case an upper case, cookie now is UPPERCASE❗️
        onClick(region.toUpperCase())
      }}
      justifyContent="flex-start"
      alignItems="center"
      width={'270px'}
      borderRadius="4px"
      py={0.5}
      marginBottom={1}
      customCss={{
        '&:hover': {
          cursor: 'pointer',
          backgroundColor: '#EAEEF4',
          '.text': {
            textDecoration: 'underline',
          },
        },
      }}
    >
      {renderRegionItem(region)}
    </Flex>
  )

  const renderSection = (title: TFunctionResult, regions: LowerRegions[]) =>
    regions && regions.length > 0 ? (
      <>
        <Typography
          variant="subtitle2"
          color="#0b1426"
          pl={isMobile ? 0 : '16px'}
          marginBottom="20px"
          customCss={{
            '&:hover': {
              cursor: 'pointer',
            },
          }}
        >
          {title}
        </Typography>
        <Box display="flex" flexWrap="wrap" ml={isMobile ? 3 : '40px'}>
          {regions.map((region: LowerRegions) => renderRegionList(region))}
        </Box>
      </>
    ) : null

  return isOpen ? (
    <Modal
      onClose={() => setOpen(false)}
      zIndex={199}
      width={[342, 342, 628]}
      px={3}
      pt={4}
      pb={'20px'}
      borderRadius={'5px'}
    >
      <Box {...restProps}>
        {renderSection(t('geolocation.select_location'), lowerRegions)}
      </Box>
    </Modal>
  ) : null
}
